import queryString from 'query-string';
import React, { FunctionComponent, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { getStaffList, resetStaffState } from '../staff.slice';
import { IStaff, IStaffFilters } from '../staff.types';
import StaffListItem from './staff-list-item/staff-list-item.component';

import { IQueryParams } from 'app.types';
import brand from 'assets/styles/variables/brand';
import fonts from 'assets/styles/variables/fonts';
import Pagination from 'components/pagination/pagination.component';
import SortIcons from 'components/sort-icons/sort-icons.component';
import { useReduxDispatch } from 'helpers/use-redux-dispatch.helper';
import { RootState } from 'modules/core/state/root.reducer';

const StyledWrapper = styled.section`
	width: 100%;
	padding: 15px;
	border-radius: 10px;
	background: ${brand.white};

	h3 {
		margin: 0;
	}
`;

const StyledHeadings = styled.div`
	display: flex;
	align-items: center;
	font-size: ${fonts.sizes.med};
	line-height: ${fonts.line_height.large};
	user-select: none;
`;

const StyledHeading = styled.div`
	display: flex;
	align-items: center;
	min-width: 150px;
	margin: 0 45px 0 0;
	cursor: pointer;

	:focus {
		outline: none;
	}

	&:nth-of-type(1) {
		width: 300px;
	}
`;

const StyledIconHeading = styled.div`
	width: 30px;
	min-width: 30px;
	margin: 0 15px 0 auto;
	display: flex;
	align-items: center;
	text-align: center;
	cursor: pointer;

	:focus {
		outline: none;
	}
`;

const StyledStaffList = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
`;

const StyledSortIcons = styled(SortIcons)`
	margin: 0 10px 0 0;
`;

/** Renders staff list component */
const StaffList: FunctionComponent = () => {
	// variables for hooks
	const dispatch = useReduxDispatch();
	const location = useLocation();
	const history = useHistory();

	// Get query params
	const query: IQueryParams = queryString.parse(location.search);

	// Get staff list
	const { staff, pagination } = useSelector((state: RootState) => {
		return state.staff;
	});

	useEffect(() => {
		// Get filters from query params
		const staffFilters: IStaffFilters = {
			accounts: query.accounts,
			brands: query.brands,
			venues: query.venues,
			search: query.search,
			sortOrder: query.sortOrder,
			sort: query.sort,
			pageNumber: parseFloat(query.pageNumber!) || 1,
		};
		const getData = async () => {
			await dispatch(getStaffList(staffFilters));
		};

		getData();
		// useEffect cleanup
		return () => {
			dispatch(resetStaffState());
		};
	}, [
		dispatch,
		query.pageNumber,
		query.accounts,
		query.brands,
		query.venues,
		query.search,
		query.sort,
		query.sortOrder,
	]);

	/** Empty message component */
	const EmptyMessage: FunctionComponent = () => {
		return (
			<h3 aria-label="staff-empty">
				<FormattedMessage id="staffList.empty" />
			</h3>
		);
	};

	// Function to update sort query params
	const updateSort = (column: keyof IStaff) => {
		// sort order from query params
		const { sortOrder } = query;
		// variable to store sort direction
		let sortDir;

		if (sortOrder === 'ASC') {
			sortDir = 'DESC';
		} else if (sortOrder === 'DESC') {
			sortDir = '';
		} else {
			sortDir = 'ASC';
		}

		// Create list of filters
		const filters = {
			...query,
			sortOrder: sortDir,
			sort: column,
		};

		// change route
		history.push(`${location.pathname}?${queryString.stringify(filters)}`);
	};

	const handlePagination = (pageNumber: number = 1) => {
		// Create list of filters
		const filters = {
			...query,
			pageNumber,
		};

		// change route
		history.push(`${location.pathname}?${queryString.stringify(filters)}`);
	};

	return (
		<StyledWrapper>
			{staff && staff.length ? (
				<>
					<StyledHeadings>
						<StyledHeading
							data-testid="sort-email"
							onClick={() => updateSort('email')}
							onKeyPress={() => updateSort('email')}
							role="button"
							tabIndex={0}
						>
							<StyledSortIcons
								active={query.sort === 'email'}
								sort={query.sortOrder}
							/>
							<FormattedMessage id="staffList.headings.email" />
						</StyledHeading>
						<StyledHeading
							data-testid="sort-name"
							onClick={() => updateSort('firstName')}
							onKeyPress={() => updateSort('firstName')}
							role="button"
							tabIndex={0}
						>
							<StyledSortIcons
								active={query.sort === 'firstName'}
								sort={query.sortOrder}
							/>
							<FormattedMessage id="staffList.headings.firstName" />
						</StyledHeading>
						<StyledHeading
							data-testid="sort-lastname"
							onClick={() => updateSort('lastName')}
							onKeyPress={() => updateSort('lastName')}
							role="button"
							tabIndex={0}
						>
							<StyledSortIcons
								active={query.sort === 'lastName'}
								sort={query.sortOrder}
							/>
							<FormattedMessage id="staffList.headings.lastName" />
						</StyledHeading>
						<StyledIconHeading>
							<FormattedMessage id="staffList.headings.edit" />
						</StyledIconHeading>
					</StyledHeadings>
					<StyledStaffList aria-label="staff-list">
						{staff.map((item) => (
							<StaffListItem item={item} key={item.id} />
						))}
					</StyledStaffList>
					{pagination && pagination.pageCount > 1 && (
						<Pagination
							pageNumber={
								query.pageNumber
									? parseFloat(query.pageNumber)
									: 1
							}
							pageCount={pagination.pageCount}
							setPageNumber={handlePagination}
						/>
					)}
				</>
			) : (
				<EmptyMessage />
			)}
		</StyledWrapper>
	);
};

export default StaffList;
